import type { ScreenerData } from "epsm-api-types/models";
import { useEffect, useState } from "react";
import {
	DEFAULT_FULL_CURRENCY_OPTIONS,
	formatValue,
	formatValueBigNumber,
	formatValueDate,
} from "@/components/widgets/helpers/FormattingHelpers";
import { formatPositivePercentValue } from "@/components/widgets/Quote/lib/helpers/formatPositivePercentValue";
import { valueColor } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { settings } from "@/lib/utils/settings/settings";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { SECURITY_CODE_DESCRIPTIONS } from "@/shared/constants/securityCodeDescriptions";
import { componentClassNames } from "../../../config";

export const QuotePriceHeader = ({
	screenerData,
}: {
	screenerData: ScreenerData | null;
}) => {
	const [price, setPrice] = useState<number | null>(null);
	const [priceChange, setPriceChange] = useState<number | null>(null);

	useEffect(() => {
		if (!screenerData) {
			setPrice(null);
			setPriceChange(null);
			return;
		}
		setPrice(screenerData?.rt_price ?? null);
		setPriceChange(screenerData?.rt_price_chg?.d1 ?? null);
		const eventSource = new EventSource(
			`${settings.wsHost}/live/monitor/${screenerData.ticker}`,
		);
		eventSource.onerror = (error) => {
			ErrorHandler.logError(error);
		};
		const processData = (data: Partial<ScreenerData>) => {
			if (data?.rt_price_chg?.d1 != null) {
				setPriceChange(data.rt_price_chg.d1);
			}
			if (data?.rt_price != null) {
				setPrice(data.rt_price);
			}
		};
		eventSource.onmessage = (event) => {
			const items = JSON.parse(event.data) as Partial<ScreenerData>[];
			for (const item of items) {
				processData(item);
			}
		};
		return () => {
			eventSource?.close();
		};
	}, [screenerData]);

	if (!screenerData) return null;

	const priceColorClass = valueColor(priceChange ?? 0);

	return (
		<div className={componentClassNames}>
			<div className="flex justify-between w-full font-semibold text-xl">
				<span data-testid="company-ticker" className="text-white">
					{screenerData?.ticker}
				</span>
				<span className={priceColorClass} data-testid="price-data">
					{formatValue(price, "num", DEFAULT_FULL_CURRENCY_OPTIONS)}{" "}
					{formatPositivePercentValue(priceChange)}
				</span>
			</div>
			<div className="flex justify-end gap-2">
				{screenerData?.adj_close_price_xd?.["0_D"] != null && (
					<span>
						Close Price:{" "}
						{formatValue(
							screenerData?.adj_close_price_xd?.["0_D"],
							"num",
							DEFAULT_FULL_CURRENCY_OPTIONS,
						)}
					</span>
				)}
				<span>
					{formatPositivePercentValue(screenerData?.adj_close_pct_chg?.["1_D"])}
				</span>
			</div>
			<p className="font-bold text-accent-foreground">{screenerData?.name}</p>
			<p data-testid="company-sector">{screenerData?.sector}</p>
			{screenerData?.code != null && (
				<p>
					Type:{" "}
					{SECURITY_CODE_DESCRIPTIONS[
						screenerData.code as keyof typeof SECURITY_CODE_DESCRIPTIONS
					] ?? screenerData.code}
				</p>
			)}
			<div className="flex justify-between gap-2 w-full">
				{screenerData?.market_cap != null && (
					<p>Market Cap {formatValueBigNumber(screenerData.market_cap)}</p>
				)}
				{screenerData?.next_rpt_date != null && (
					<p className="font-bold">
						Next Earnings: {formatValueDate(screenerData?.next_rpt_date)}
					</p>
				)}
			</div>
		</div>
	);
};
